import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  TrendingUp, TrendingDown, DollarSign, AlertTriangle, CheckCircle2, XCircle,
  Clock, Building2, Users, FileText, Shield, Banknote, Activity, ChevronRight,
  Briefcase, Eye, Send, ArrowUpRight, ArrowDownRight, Wallet, Hourglass,
  AlertCircle, Award, Target, BarChart3,
} from 'lucide-react';

/**
 * FINANCIAL COMMAND CENTER — Visual Mockup Preview
 * Public route /financial-command-center-preview, no auth required.
 * Shows 4 layout options (a / b / c / d) as tabs.
 */

// ---------- Hard-coded mock data ----------
const MOCK_BURN = { perDay: 147.32, perHour: 6.14, weekToDate: 1031, monthToDate: 4419 };
const MOCK_FUNDS = { totalLoan: 2_600_000, drawn: 1_870_000, available: 730_000, pendingDraw: 142_500 };

const MOCK_CONTRACTORS = [
  { id: 'c1', name: 'Lowden Plumbing & Heating', trade: 'HVAC / Plumbing', w9: 'approved', coi: 'approved', coiExpires: '2026-08-15', license: 'approved', lien_waiver: 'pending', amount: 18462 },
  { id: 'c2', name: 'Apex Framing LLC', trade: 'Framing', w9: 'approved', coi: 'expiring', coiExpires: '2026-06-01', license: 'approved', lien_waiver: 'approved', amount: 42000 },
  { id: 'c3', name: 'BlueSky Roofing', trade: 'Roofing', w9: 'submitted', coi: 'awaiting', coiExpires: null, license: 'awaiting', lien_waiver: 'awaiting', amount: 18500 },
  { id: 'c4', name: 'Iowa Concrete Co.', trade: 'Foundation', w9: 'approved', coi: 'approved', coiExpires: '2027-01-12', license: 'approved', lien_waiver: 'approved', amount: 22000 },
  { id: 'c5', name: 'Cedar Electric', trade: 'Electrical', w9: 'awaiting', coi: 'awaiting', coiExpires: null, license: 'submitted', lien_waiver: 'awaiting', amount: 14000 },
  { id: 'c6', name: 'Prime Drywall', trade: 'Drywall', w9: 'approved', coi: 'approved', coiExpires: '2026-11-20', license: 'approved', lien_waiver: 'submitted', amount: 9800 },
  { id: 'c7', name: 'Summit Painting', trade: 'Paint', w9: 'submitted', coi: 'submitted', coiExpires: null, license: 'awaiting', lien_waiver: 'awaiting', amount: 7500 },
];

const MOCK_PROJECTS_PORTFOLIO = [
  { id: 'p1', name: 'New Build 38th', address: 'Cedar Rapids, IA', total: 2_600_000, drawn: 1_870_000, burn: 147, score: 82, complianceIssues: 2 },
  { id: 'p2', name: 'Solon Modern Farmhouse', address: 'Solon, IA', total: 1_400_000, drawn: 312_000, burn: 78, score: 91, complianceIssues: 0 },
  { id: 'p3', name: 'Iowa City Renovation', address: 'Iowa City, IA', total: 480_000, drawn: 410_000, burn: 24, score: 95, complianceIssues: 0 },
  { id: 'p4', name: 'Marion Spec Home', address: 'Marion, IA', total: 720_000, drawn: 110_000, burn: 41, score: 65, complianceIssues: 5 },
];

const MOCK_BANK_PORTFOLIO = [
  { id: 'b1', borrower: 'Mod Williams', project: 'New Build 38th', loan: 2_600_000, drawn: 1_870_000, lastDraw: '12 days ago', risk: 'low', drawsPending: 0 },
  { id: 'b2', borrower: 'Sara Lee Homes', project: 'Westside Duplex', loan: 980_000, drawn: 740_000, lastDraw: '4 days ago', risk: 'low', drawsPending: 1 },
  { id: 'b3', borrower: 'Apex Builders', project: 'Maple Ridge Estate', loan: 1_750_000, drawn: 1_580_000, lastDraw: '38 days ago', risk: 'medium', drawsPending: 1 },
  { id: 'b4', borrower: 'NW Real Estate', project: 'Elm Street Spec', loan: 540_000, drawn: 510_000, lastDraw: '6 days ago', risk: 'low', drawsPending: 0 },
  { id: 'b5', borrower: 'Greg Foster', project: 'Hidden Valley Custom', loan: 1_200_000, drawn: 290_000, lastDraw: '67 days ago', risk: 'high', drawsPending: 0 },
];

// ---------- Tiny helpers ----------
const fmt$ = (n) => '$' + (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtFull$ = (n) => '$' + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const StatusPill = ({ status }) => {
  const map = {
    approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
    submitted: { label: 'Submitted', cls: 'bg-blue-100 text-blue-700 border-blue-200', Icon: Send },
    awaiting: { label: 'Awaiting upload', cls: 'bg-amber-100 text-amber-700 border-amber-200', Icon: Hourglass },
    expiring: { label: 'Expiring soon', cls: 'bg-orange-100 text-orange-700 border-orange-200', Icon: AlertTriangle },
    pending: { label: 'Pending', cls: 'bg-slate-100 text-slate-700 border-slate-200', Icon: Clock },
    expired: { label: 'Expired', cls: 'bg-red-100 text-red-700 border-red-200', Icon: XCircle },
  };
  const m = map[status] || map.pending;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md border ${m.cls}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
};

// ---------- Live daily burn widget ----------
const LiveBurnWidget = ({ compact = false }) => (
  <Card className="border-2 border-red-200 overflow-hidden">
    <div className="bg-gradient-to-br from-red-500 to-orange-600 p-4 text-white">
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wide opacity-90 flex items-center gap-1.5">
          <Activity className="w-3.5 h-3.5 animate-pulse" /> Daily Burn
        </div>
        <div className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full">LIVE</div>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-3xl font-bold tracking-tight">${MOCK_BURN.perDay}</span>
        <span className="text-sm opacity-90">/ day</span>
      </div>
      <div className="text-[11px] opacity-90 mt-0.5">${MOCK_BURN.perHour.toFixed(2)} per hour · accruing now</div>
    </div>
    {!compact && (
      <div className="p-3 grid grid-cols-2 gap-2 bg-white">
        <div>
          <div className="text-[10px] text-slate-500 uppercase">Week to date</div>
          <div className="text-sm font-bold text-slate-900">{fmt$(MOCK_BURN.weekToDate)}</div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 uppercase">Month to date</div>
          <div className="text-sm font-bold text-slate-900">{fmt$(MOCK_BURN.monthToDate)}</div>
        </div>
      </div>
    )}
  </Card>
);

// ---------- Total funds widget ----------
const TotalFundsWidget = () => {
  const drawnPct = (MOCK_FUNDS.drawn / MOCK_FUNDS.totalLoan) * 100;
  return (
    <Card className="border-2 border-emerald-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase text-emerald-700 flex items-center gap-1.5">
          <Wallet className="w-3.5 h-3.5" /> Total Funds
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="text-3xl font-bold text-slate-900 tracking-tight">{fmt$(MOCK_FUNDS.available)}</div>
          <div className="text-xs text-slate-500">available · {fmt$(MOCK_FUNDS.totalLoan)} total loan</div>
        </div>
        <div className="space-y-1">
          <div className="flex justify-between text-[11px] text-slate-600">
            <span>Drawn</span><span className="font-semibold">{fmt$(MOCK_FUNDS.drawn)} ({drawnPct.toFixed(0)}%)</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full" style={{ width: `${drawnPct}%` }} />
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 text-[11px] text-amber-800 flex items-center gap-1.5">
          <Hourglass className="w-3 h-3" /> {fmt$(MOCK_FUNDS.pendingDraw)} pending draw approval
        </div>
      </CardContent>
    </Card>
  );
};

// ---------- Contractor compliance widget (scrollable) ----------
const ContractorComplianceWidget = ({ height = 'h-[420px]', title = 'Contractor Compliance' }) => {
  const totalCompliance = MOCK_CONTRACTORS.length * 4; // w9, coi, license, lien_waiver
  let approved = 0;
  MOCK_CONTRACTORS.forEach(c => {
    ['w9','coi','license','lien_waiver'].forEach(k => { if (c[k] === 'approved') approved++; });
  });
  const pct = Math.round((approved / totalCompliance) * 100);

  return (
    <Card className="border-2 border-blue-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xs uppercase text-blue-700 flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5" /> {title}
          </CardTitle>
          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{pct}% complete</Badge>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-2">
          <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-500" style={{ width: `${pct}%` }} />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className={`overflow-y-auto ${height} divide-y`}>
          {MOCK_CONTRACTORS.map((c) => (
            <div key={c.id} className="px-4 py-3 hover:bg-slate-50 transition-colors">
              <div className="flex items-center justify-between mb-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm text-slate-900 truncate">{c.name}</div>
                  <div className="text-[11px] text-slate-500">{c.trade} · {fmt$(c.amount)}</div>
                </div>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]">
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <div className="flex items-center justify-between bg-slate-50 rounded px-2 py-1">
                  <span className="text-[10px] font-bold text-slate-600 uppercase">W-9</span>
                  <StatusPill status={c.w9} />
                </div>
                <div className="flex items-center justify-between bg-slate-50 rounded px-2 py-1">
                  <span className="text-[10px] font-bold text-slate-600 uppercase">COI</span>
                  <StatusPill status={c.coi} />
                </div>
                <div className="flex items-center justify-between bg-slate-50 rounded px-2 py-1">
                  <span className="text-[10px] font-bold text-slate-600 uppercase">License</span>
                  <StatusPill status={c.license} />
                </div>
                <div className="flex items-center justify-between bg-slate-50 rounded px-2 py-1">
                  <span className="text-[10px] font-bold text-slate-600 uppercase">Lien Waiver</span>
                  <StatusPill status={c.lien_waiver} />
                </div>
              </div>
              {c.coi === 'expiring' && (
                <div className="mt-1.5 text-[10px] text-orange-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> COI expires {c.coiExpires}
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================
// Option A — Owner-Builder Cockpit (UPDATED with portfolio sections)
// ============================================================
const OptionA = () => {
  const totalCapital = MOCK_PROJECTS_PORTFOLIO.reduce((s, p) => s + p.total, 0);
  const totalDrawn = MOCK_PROJECTS_PORTFOLIO.reduce((s, p) => s + p.drawn, 0);
  const totalIssues = MOCK_PROJECTS_PORTFOLIO.reduce((s, p) => s + p.complianceIssues, 0);
  const drawsPending = 2; // mock

  return (
    <div className="space-y-6">
      {/* HERO - current project deep dive */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Owner-builder cockpit · Active project</div>
            <h2 className="text-2xl font-bold">New Build 38th</h2>
            <div className="text-sm text-slate-400 mt-0.5">Cedar Rapids, IA · Phase 4 of 8 · 25% complete</div>
          </div>
          <div className="flex gap-2">
            <Button className="bg-emerald-500 hover:bg-emerald-600 text-white"><Banknote className="w-4 h-4 mr-1.5" /> Request Draw</Button>
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10"><FileText className="w-4 h-4 mr-1.5" /> Bank Report</Button>
          </div>
        </div>
      </div>

      {/* HERO WIDGETS - current project */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <LiveBurnWidget />
        <TotalFundsWidget />
        <Card className="border-2 border-purple-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase text-purple-700 flex items-center gap-1.5">
              <Award className="w-3.5 h-3.5" /> Bank-Ready Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="relative w-20 h-20">
                <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
                  <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#e5e7eb" strokeWidth="3"/>
                  <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#a855f7" strokeWidth="3" strokeDasharray="82, 100"/>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-slate-900">82</div>
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">Almost ready</div>
                <div className="text-[11px] text-slate-500 mt-0.5">2 contractor docs missing for current draw</div>
                <Button size="sm" variant="outline" className="mt-2 h-7 text-[11px]">View details</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* PORTFOLIO STRIP - your-projects rollup */}
      <div className="border-t-2 border-dashed border-slate-200 pt-6">
        <div className="flex items-center gap-2 mb-3">
          <Briefcase className="w-4 h-4 text-slate-700" />
          <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">Your Portfolio</h3>
          <span className="text-xs text-slate-400">· all {MOCK_PROJECTS_PORTFOLIO.length} active projects</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card><CardContent className="p-4">
            <div className="text-[11px] uppercase text-slate-500 font-bold">Active Loans</div>
            <div className="text-2xl font-bold text-slate-900 mt-1">{MOCK_PROJECTS_PORTFOLIO.length}</div>
            <div className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1"><ArrowUpRight className="w-3 h-3" /> +1 this month</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-[11px] uppercase text-slate-500 font-bold">Capital Deployed</div>
            <div className="text-2xl font-bold text-slate-900 mt-1">{fmt$(totalDrawn)}</div>
            <div className="text-[11px] text-slate-500 mt-1">of {fmt$(totalCapital)}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-[11px] uppercase text-slate-500 font-bold">Draws Pending</div>
            <div className="text-2xl font-bold text-amber-600 mt-1">{drawsPending}</div>
            <div className="text-[11px] text-slate-500 mt-1">awaiting bank approval</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-[11px] uppercase text-slate-500 font-bold">Compliance Issues</div>
            <div className="text-2xl font-bold text-amber-600 mt-1">{totalIssues}</div>
            <div className="text-[11px] text-slate-500 mt-1">across portfolio</div>
          </CardContent></Card>
        </div>
      </div>

      {/* PROJECT RANKINGS - removed. Burn/day + Score now live in Active Loans table below. */}

      {/* ACTIVE LOANS TABLE - your loans across portfolio */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Banknote className="w-4 h-4 text-blue-600" /> Active Loans &amp; Draw Status
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left p-3">Project</th>
                <th className="text-right p-3">Loan</th>
                <th className="text-right p-3">Drawn</th>
                <th className="text-right p-3">Burn / day</th>
                <th className="text-center p-3">Score</th>
                <th className="text-left p-3">Last Draw</th>
                <th className="text-center p-3">Status</th>
                <th className="text-center p-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {MOCK_PROJECTS_PORTFOLIO.map((p, idx) => {
                const lastDraws = ['12 days ago', '4 days ago', '38 days ago', '67 days ago'];
                const status = idx === 0 ? 'on-track' : idx === 1 ? 'pending-review' : idx === 2 ? 'stale' : 'pending-review';
                return (
                  <tr key={p.id} className="hover:bg-slate-50">
                    <td className="p-3">
                      <div className="font-semibold text-slate-900">{p.name}</div>
                      <div className="text-xs text-slate-500">{p.address}</div>
                    </td>
                    <td className="p-3 text-right font-mono text-slate-900">{fmt$(p.total)}</td>
                    <td className="p-3 text-right font-mono">
                      <div>{fmt$(p.drawn)}</div>
                      <div className="text-[10px] text-slate-500">{Math.round((p.drawn/p.total)*100)}%</div>
                    </td>
                    <td className="p-3 text-right font-mono text-red-600 font-semibold">${p.burn}</td>
                    <td className="p-3 text-center">
                      <span className={`inline-block font-bold px-2 py-0.5 rounded-md text-xs ${
                        p.score >= 90 ? 'bg-emerald-100 text-emerald-700' :
                        p.score >= 75 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {p.score}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-slate-600">{lastDraws[idx]}</td>
                    <td className="p-3 text-center">
                      <Badge className={
                        status === 'on-track' ? 'bg-emerald-100 text-emerald-700' :
                        status === 'pending-review' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }>
                        {status === 'pending-review' ? 'pending review' : status}
                      </Badge>
                    </td>
                    <td className="p-3 text-center">
                      {status === 'pending-review' ? (
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs h-7">View</Button>
                      ) : (
                        <Button size="sm" variant="ghost" className="text-xs h-7"><Eye className="w-3 h-3" /></Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* CONTRACTOR COMPLIANCE */}
      <ContractorComplianceWidget />
    </div>
  );
};

// ============================================================
// Option B — Bank-Facing Loan Officer Dashboard
// ============================================================
const OptionB = () => {
  const portfolioTotal = MOCK_BANK_PORTFOLIO.reduce((s, b) => s + b.loan, 0);
  const portfolioDrawn = MOCK_BANK_PORTFOLIO.reduce((s, b) => s + b.drawn, 0);
  const drawsPending = MOCK_BANK_PORTFOLIO.reduce((s, b) => s + b.drawsPending, 0);
  const highRisk = MOCK_BANK_PORTFOLIO.filter(b => b.risk === 'high').length;

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-blue-700 to-indigo-800 text-white rounded-xl p-6">
        <div className="text-xs uppercase tracking-wide text-blue-200 mb-1">Bank · Construction Loan Portfolio</div>
        <h2 className="text-2xl font-bold">Hills Bank — Cedar Rapids Branch</h2>
        <div className="text-sm text-blue-200 mt-0.5">Sarah Connor · Construction Loan Officer</div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><div className="text-[11px] uppercase text-slate-500 font-bold">Active Loans</div><div className="text-2xl font-bold text-slate-900 mt-1">{MOCK_BANK_PORTFOLIO.length}</div><div className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1"><ArrowUpRight className="w-3 h-3" /> +1 this month</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-[11px] uppercase text-slate-500 font-bold">Capital Deployed</div><div className="text-2xl font-bold text-slate-900 mt-1">{fmt$(portfolioDrawn)}</div><div className="text-[11px] text-slate-500 mt-1">of {fmt$(portfolioTotal)}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-[11px] uppercase text-slate-500 font-bold">Draws Pending</div><div className="text-2xl font-bold text-amber-600 mt-1">{drawsPending}</div><div className="text-[11px] text-slate-500 mt-1">awaiting your review</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-[11px] uppercase text-slate-500 font-bold">High-Risk Borrowers</div><div className="text-2xl font-bold text-red-600 mt-1">{highRisk}</div><div className="text-[11px] text-red-600 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Stale draws</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Briefcase className="w-4 h-4 text-blue-600" /> Active Borrower Portfolio</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left p-3">Borrower / Project</th>
                <th className="text-right p-3">Loan</th>
                <th className="text-right p-3">Drawn</th>
                <th className="text-left p-3">Last Draw</th>
                <th className="text-center p-3">Risk</th>
                <th className="text-center p-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {MOCK_BANK_PORTFOLIO.map(b => (
                <tr key={b.id} className="hover:bg-slate-50">
                  <td className="p-3">
                    <div className="font-semibold text-slate-900">{b.borrower}</div>
                    <div className="text-xs text-slate-500">{b.project}</div>
                  </td>
                  <td className="p-3 text-right font-mono text-slate-900">{fmt$(b.loan)}</td>
                  <td className="p-3 text-right font-mono">
                    <div>{fmt$(b.drawn)}</div>
                    <div className="text-[10px] text-slate-500">{Math.round((b.drawn/b.loan)*100)}%</div>
                  </td>
                  <td className="p-3 text-xs text-slate-600">{b.lastDraw}</td>
                  <td className="p-3 text-center">
                    <Badge className={
                      b.risk === 'low' ? 'bg-emerald-100 text-emerald-700' :
                      b.risk === 'medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }>{b.risk}</Badge>
                  </td>
                  <td className="p-3 text-center">
                    {b.drawsPending > 0 ? (
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs h-7">Review draw</Button>
                    ) : (
                      <Button size="sm" variant="ghost" className="text-xs h-7"><Eye className="w-3 h-3" /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================
// Option C — CFO / Multi-Project Portfolio
// ============================================================
const OptionC = () => {
  const totalCapital = MOCK_PROJECTS_PORTFOLIO.reduce((s, p) => s + p.total, 0);
  const totalDrawn = MOCK_PROJECTS_PORTFOLIO.reduce((s, p) => s + p.drawn, 0);
  const totalBurn = MOCK_PROJECTS_PORTFOLIO.reduce((s, p) => s + p.burn, 0);
  const totalIssues = MOCK_PROJECTS_PORTFOLIO.reduce((s, p) => s + p.complianceIssues, 0);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-emerald-700 to-teal-900 text-white rounded-xl p-6">
        <div className="text-xs uppercase tracking-wide text-emerald-200 mb-1">CFO View · Multi-project rollup</div>
        <h2 className="text-2xl font-bold">Williams Development Co.</h2>
        <div className="text-sm text-emerald-200 mt-0.5">{MOCK_PROJECTS_PORTFOLIO.length} active projects · {fmt$(totalCapital)} under management</div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><div className="text-[11px] uppercase text-slate-500 font-bold">Capital Deployed</div><div className="text-2xl font-bold text-slate-900 mt-1">{fmt$(totalDrawn)}</div><div className="text-[11px] text-slate-500 mt-1">{((totalDrawn/totalCapital)*100).toFixed(0)}% of total</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-[11px] uppercase text-slate-500 font-bold">Daily Burn (all projects)</div><div className="text-2xl font-bold text-red-600 mt-1">${totalBurn}</div><div className="text-[11px] text-slate-500 mt-1">/ day combined</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-[11px] uppercase text-slate-500 font-bold">Available Capital</div><div className="text-2xl font-bold text-emerald-600 mt-1">{fmt$(totalCapital - totalDrawn)}</div><div className="text-[11px] text-slate-500 mt-1">to deploy</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-[11px] uppercase text-slate-500 font-bold">Compliance Issues</div><div className="text-2xl font-bold text-amber-600 mt-1">{totalIssues}</div><div className="text-[11px] text-slate-500 mt-1">across portfolio</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Target className="w-4 h-4 text-emerald-600" /> Project Rankings</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {[...MOCK_PROJECTS_PORTFOLIO].sort((a,b) => b.score - a.score).map((p, i) => (
              <div key={p.id} className="p-4 flex items-center gap-4 hover:bg-slate-50">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                  i === 0 ? 'bg-yellow-500' : i === 1 ? 'bg-slate-400' : i === 2 ? 'bg-orange-500' : 'bg-slate-300'
                }`}>{i+1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.address}</div>
                </div>
                <div className="text-right hidden md:block">
                  <div className="text-xs text-slate-500">Drawn</div>
                  <div className="text-sm font-bold text-slate-900">{fmt$(p.drawn)}</div>
                </div>
                <div className="text-right hidden md:block">
                  <div className="text-xs text-slate-500">Burn/day</div>
                  <div className="text-sm font-bold text-red-600">${p.burn}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">Score</div>
                  <div className={`text-sm font-bold ${p.score >= 90 ? 'text-emerald-600' : p.score >= 75 ? 'text-amber-600' : 'text-red-600'}`}>{p.score}</div>
                </div>
                {p.complianceIssues > 0 && (
                  <Badge className="bg-amber-100 text-amber-700">{p.complianceIssues} issues</Badge>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <ContractorComplianceWidget title="Portfolio-wide Contractor Compliance" />
    </div>
  );
};

// ============================================================
// Option D — All Three (Tabbed Switcher)
// ============================================================
const OptionD = () => {
  const [view, setView] = useState('borrower');
  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide opacity-80 mb-1">Universal Command Center</div>
            <h2 className="text-2xl font-bold">Switch perspective in one click</h2>
          </div>
          <div className="bg-white/15 backdrop-blur rounded-full p-1 flex gap-1 text-sm">
            <button onClick={() => setView('borrower')} className={`px-4 py-1.5 rounded-full font-semibold transition ${view==='borrower'?'bg-white text-orange-600':'text-white'}`}>Borrower</button>
            <button onClick={() => setView('bank')} className={`px-4 py-1.5 rounded-full font-semibold transition ${view==='bank'?'bg-white text-orange-600':'text-white'}`}>Bank</button>
            <button onClick={() => setView('cfo')} className={`px-4 py-1.5 rounded-full font-semibold transition ${view==='cfo'?'bg-white text-orange-600':'text-white'}`}>Portfolio</button>
          </div>
        </div>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
        {view === 'borrower' && (
          <div className="space-y-4">
            <div className="text-sm text-slate-600 italic">📊 Showing single-project owner-builder view</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LiveBurnWidget compact />
              <TotalFundsWidget />
            </div>
            <ContractorComplianceWidget height="h-[300px]" />
          </div>
        )}
        {view === 'bank' && (
          <div className="space-y-4">
            <div className="text-sm text-slate-600 italic">🏦 Showing loan-officer portfolio view</div>
            <Card>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="text-left p-2">Borrower</th><th className="text-right p-2">Drawn</th><th className="text-center p-2">Risk</th><th className="text-center p-2">Action</th></tr></thead>
                  <tbody className="divide-y">
                    {MOCK_BANK_PORTFOLIO.slice(0,4).map(b => (
                      <tr key={b.id}><td className="p-2 font-semibold">{b.borrower}<div className="text-[10px] text-slate-500">{b.project}</div></td><td className="p-2 text-right">{fmt$(b.drawn)}</td><td className="p-2 text-center"><Badge className={b.risk==='low'?'bg-emerald-100 text-emerald-700':b.risk==='medium'?'bg-amber-100 text-amber-700':'bg-red-100 text-red-700'}>{b.risk}</Badge></td><td className="p-2 text-center">{b.drawsPending>0 ? <Button size="sm" className="h-6 text-[10px]">Review</Button> : '—'}</td></tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}
        {view === 'cfo' && (
          <div className="space-y-4">
            <div className="text-sm text-slate-600 italic">📈 Showing multi-project portfolio rollup</div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Card><CardContent className="p-3"><div className="text-[10px] uppercase text-slate-500 font-bold">Total deployed</div><div className="text-xl font-bold mt-1">{fmt$(MOCK_PROJECTS_PORTFOLIO.reduce((s,p)=>s+p.drawn,0))}</div></CardContent></Card>
              <Card><CardContent className="p-3"><div className="text-[10px] uppercase text-slate-500 font-bold">Daily burn</div><div className="text-xl font-bold text-red-600 mt-1">${MOCK_PROJECTS_PORTFOLIO.reduce((s,p)=>s+p.burn,0)}</div></CardContent></Card>
              <Card><CardContent className="p-3"><div className="text-[10px] uppercase text-slate-500 font-bold">Active</div><div className="text-xl font-bold mt-1">{MOCK_PROJECTS_PORTFOLIO.length}</div></CardContent></Card>
              <Card><CardContent className="p-3"><div className="text-[10px] uppercase text-slate-500 font-bold">Issues</div><div className="text-xl font-bold text-amber-600 mt-1">{MOCK_PROJECTS_PORTFOLIO.reduce((s,p)=>s+p.complianceIssues,0)}</div></CardContent></Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// PAGE
// ============================================================
export default function FinancialCommandCenterPreview() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        {/* Hero */}
        <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 rounded-2xl p-8 text-white shadow-xl">
          <div className="flex items-center gap-3 mb-2">
            <BarChart3 className="w-8 h-8 text-amber-400" />
            <Badge className="bg-amber-400 text-slate-900 hover:bg-amber-400">PREVIEW · NOT YET BUILT</Badge>
          </div>
          <h1 className="text-4xl font-bold">Financial Command Center</h1>
          <p className="text-lg text-slate-300 mt-2 max-w-2xl">
            One screen. Live daily burn. Total funds. Contractor compliance — at a glance.
            Pick the layout that matches your vision and I'll build it.
          </p>
          <div className="flex flex-wrap gap-2 mt-4 text-sm">
            <span className="bg-white/15 backdrop-blur rounded-full px-3 py-1">⏱ Live burn $/day</span>
            <span className="bg-white/15 backdrop-blur rounded-full px-3 py-1">💰 Funds available</span>
            <span className="bg-white/15 backdrop-blur rounded-full px-3 py-1">🛡 Contractor W-9 / COI / Lien Waiver / License</span>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="a" className="w-full">
          <TabsList className="grid grid-cols-2 lg:grid-cols-4 w-full h-auto bg-white border border-slate-200 p-1">
            <TabsTrigger value="a" className="text-left flex-col items-start py-2 data-[state=active]:bg-amber-50 data-[state=active]:text-amber-700">
              <span className="text-xs font-bold">Option A</span>
              <span className="text-[10px] opacity-70">Owner-builder cockpit</span>
            </TabsTrigger>
            <TabsTrigger value="b" className="text-left flex-col items-start py-2 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              <span className="text-xs font-bold">Option B</span>
              <span className="text-[10px] opacity-70">Bank loan-officer</span>
            </TabsTrigger>
            <TabsTrigger value="c" className="text-left flex-col items-start py-2 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700">
              <span className="text-xs font-bold">Option C</span>
              <span className="text-[10px] opacity-70">CFO multi-project</span>
            </TabsTrigger>
            <TabsTrigger value="d" className="text-left flex-col items-start py-2 data-[state=active]:bg-orange-50 data-[state=active]:text-orange-700">
              <span className="text-xs font-bold">Option D</span>
              <span className="text-[10px] opacity-70">Tabbed all-in-one</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="a" className="mt-6"><OptionA /></TabsContent>
          <TabsContent value="b" className="mt-6"><OptionB /></TabsContent>
          <TabsContent value="c" className="mt-6"><OptionC /></TabsContent>
          <TabsContent value="d" className="mt-6"><OptionD /></TabsContent>
        </Tabs>

        {/* CTA */}
        <div className="bg-slate-900 text-white rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold">Pick your favorite</h2>
          <p className="text-slate-300 mt-2">Reply with the letter (A / B / C / D) or a combination, and I'll build the real version.</p>
        </div>
      </div>
    </div>
  );
}
